import React, { Component } from 'react';
import { styled } from '@mui/material/styles';
import Axios from 'axios';
import _ from 'lodash';

import { API_URL } from '../../utils/config/config';
import { SFDC_STAGE_OPTIONS, SFDC_DIVISIONS, SFDC_COMPANIES } from '../../components/post-security/common/constants';

import Header from '../../components/post-security/common/header';
import Sidebar from '../../components/post-security/common/sidebar';
import PMs from '../../components/post-security/pm_view/pms.js';
import PMSidebar from '../../components/post-security/pm_view/pmSidebar.js';
import Footer from '../../components/post-security/common/footer';
import ProjectDetailedViewContainer from './ProjectDetailedViewContainer';

import { Typography, Button, Paper, Dialog } from '@mui/material';
import { Replay } from '@mui/icons-material';

const PREFIX = 'PMsContainer';

const classes = {
    root_grid: `${PREFIX}-root_grid`,
    main: `${PREFIX}-main`,
    pm_view: `${PREFIX}-pm_view`,
    header: `${PREFIX}-header`
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')((
    {
        theme
    }
) => ({
    [`& .${classes.root_grid}`]: {
        display: 'grid',
        height: '100vh',
        'grid-template-rows': '4em auto 2em',
        overflowY: 'visible',
    },

    [`& .${classes.main}`]: {
    paddingLeft: '70px',
    paddingTop: '20px',
    paddingRight: '70px',
    },

    [`& .${classes.pm_view}`]: {
        'maxWidth': 'calc(100vw - 70px - 60px - 30px)',
        overflowX: 'auto',
    },

    [`& .${classes.header}`]: {
        zIndex: 999999,
    }
}));

class PMsContainer extends Component {
    // Filter configuration constants
    static FILTER_MAPPINGS = {
        'divisions_pm': 'division',
        'supervisors_pm': 'supervisor', 
        'locations_pm': 'location_short',
        'accounts_job': 'sfdc_account_name',
        'stages_job': 'stage',
        'companies_job': ['sfdc_company', 'sfdc_second_fa_company', 'sfdc_third_fa_company', 'sfdc_fourth_fa_company'],
        'divisions_job': ['sfdc_division', 'sfdc_second_fa_division', 'sfdc_third_fa_division', 'sfdc_fourth_fa_division']
    };

    static PM_FILTERS = ['divisions_pm', 'supervisors_pm', 'locations_pm'];
    static SINGLE_FIELD_JOB_FILTERS = ['stages_job', 'accounts_job'];
    static MULTI_FIELD_JOB_FILTERS = ['divisions_job', 'companies_job'];
    static REQUIRED_PROJECT_FIELDS = ['sfdc_groundbreaking', 'sfdc_project_completion_date'];

    constructor(props) {
        super(props);
        this.state = {
            pm_view_data: [],
            filtered_pm_view_data: [],
            loading: true,
            error: false,
            offline: false,
            filters: {'divisions_pm': [], 'supervisors_pm': [], 'locations_pm': [], 'companies_job':[], 'stages_job':[], 'divisions_job':[], 'accounts_job':[], 'multiply_by_sfdc_probability': true, 'use_custom_list': false, 'custom_list': []},
            sort_by: 'sort_by_number_of_jobs',
            profile_data: [],
            divisions_pm_list: [],
            supervisors_pm_list: [],
            locations_pm_list: [],
            divisions_job_list: [],
            accounts_job_list: [],
            companies_job_list: [],
            stages_job_list: [],
            showDetailedView: false,
            detail_view_project_info: null,
            pmResourceNames: [{id: '0', name: 'Loading....'}],
        };
        this.updateFilters = this.updateFilters.bind(this);
        this.applyFilters = this.applyFilters.bind(this);
        this.toggleDetailView = this.toggleDetailView.bind(this)
        this.updateDetailView = this.updateDetailView.bind(this);
        this.fetchPMViewData = this.fetchPMViewData.bind(this);
        this.handleSortByChange = this.handleSortByChange.bind(this);
    }

    // Helper method to get authenticated headers
    getAuthHeaders = async () => {
        const { getAccessTokenSilently } = this.props.auth;
        const token = await getAccessTokenSilently();
        return {
            withCredentials: true,
            headers: {
                'content-type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
        };
    }

    // Helper methods for filtering
    filterByCustomList = (data, customIds) => {
        if (customIds.length === 0) return data;
        
        const idSet = new Set(customIds.map(id => parseInt(id)));
        return data.filter(item => idSet.has(item.id));
    }

    filterPMsByField = (data, field, values) => {
        return data.filter(pm => values.includes(pm[field]));
    }

    filterJobsBySingleField = (data, field, values) => {
        return data.map(pm => ({
            ...pm,
            requirements_assigned: pm.requirements_assigned.filter(job => 
                values.includes(job[field])
            )
        })).filter(pm => pm.requirements_assigned.length > 0);
    }

    filterJobsByMultipleFields = (data, fields, values) => {
        return data.map(pm => ({
            ...pm,
            requirements_assigned: pm.requirements_assigned.filter(job =>
                fields.some(field => values.includes(job[field]))
            )
        })).filter(pm => pm.requirements_assigned.length > 0);
    }

    removeIncompleteProjects = (data) => {
        return data.map(pm => ({
            ...pm,
            requirements_assigned: pm.requirements_assigned.filter(job =>
                PMsContainer.REQUIRED_PROJECT_FIELDS.every(field => job[field])
            )
        }));
    }

    sortByAssignmentCount = (data) => {
        return data.sort((a, b) => b.requirements_assigned.length - a.requirements_assigned.length);
    }

    toggleDetailView = (boo) => {
        this.setState({ showDetailedView: boo, detail_view_project_info: null});
    }

    updateDetailView = async (value) => {
        if (!value) return;
        
        try {
            const authHeaders = await this.getAuthHeaders();
            const response = await Axios.get(`${API_URL}project_view/${value}/`, authHeaders);
            
            this.setState({
                detail_view_project_info: response.data,
                showDetailedView: true,
            });
        } catch (error) {
            console.error('Error fetching project details:', error);
            // TODO: Add user-friendly error notification
        }
    }

    // Update the filters in state as well as in DB
    updateFilters = async (filter_type, new_values) => {
        const new_filters = { ...this.state.filters, [filter_type]: new_values };
        this.setState({ filters: new_filters });

        try {
            const authHeaders = await this.getAuthHeaders();
            const data = {
                user_id: localStorage.getItem('id'),
                filters: new_filters
            };

            await Axios.patch(
                `${API_URL}pm_view_filters/${localStorage.getItem('id')}/`,
                data,
                authHeaders
            );

            this.applyFilters(new_filters);
        } catch (error) {
            console.error('Error updating filters:', error);
            // TODO: Add user-friendly error notification
        }
    }

    // Apply the filters
    applyFilters = (filters) => {
        // Early return if no data
        if (!this.state.pm_view_data || this.state.pm_view_data.length === 0) {
            this.setState({ filtered_pm_view_data: [] });
            return;
        }

        let filtered_data = _.cloneDeep(this.state.pm_view_data);

        // Handle custom list filtering first (most restrictive)
        if (filters.use_custom_list && filters.custom_list?.length > 0) {
            filtered_data = this.filterByCustomList(filtered_data, filters.custom_list);
        }

        // Apply each filter type
        for (const [filterKey, filterValues] of Object.entries(filters)) {
            // Skip empty filters and non-filterable properties
            if (!Array.isArray(filterValues) || filterValues.length === 0) continue;
            
            const mappedField = PMsContainer.FILTER_MAPPINGS[filterKey];
            if (!mappedField) continue;

            // Apply appropriate filter based on filter type
            if (PMsContainer.PM_FILTERS.includes(filterKey)) {
                filtered_data = this.filterPMsByField(filtered_data, mappedField, filterValues);
            } 
            else if (PMsContainer.SINGLE_FIELD_JOB_FILTERS.includes(filterKey)) {
                filtered_data = this.filterJobsBySingleField(filtered_data, mappedField, filterValues);
            } 
            else if (PMsContainer.MULTI_FIELD_JOB_FILTERS.includes(filterKey)) {
                filtered_data = this.filterJobsByMultipleFields(filtered_data, mappedField, filterValues);
            }
        }

        // Remove incomplete projects and sort
        filtered_data = this.removeIncompleteProjects(filtered_data);
        
        if (!filters.use_custom_list) {
            filtered_data = this.sortByAssignmentCount(filtered_data);
        }

        this.setState({ filtered_pm_view_data: filtered_data });
    }

    handleSortByChange = () => {
        const filtered_data = _.cloneDeep(this.state.filtered_pm_view_data);
        
        const isCurrentlySortingByName = this.state.sort_by === 'sort_by_name';
        
        if (isCurrentlySortingByName) {
            // Sort by number of jobs (descending)
            filtered_data.sort((a, b) => b.requirements_assigned.length - a.requirements_assigned.length);
            this.setState({ 
                sort_by: 'sort_by_number_of_jobs',
                filtered_pm_view_data: filtered_data 
            });
        } else {
            // Sort by name (ascending)
            filtered_data.sort((a, b) => a.sfdc_resource_name.localeCompare(b.sfdc_resource_name));
            this.setState({ 
                sort_by: 'sort_by_name',
                filtered_pm_view_data: filtered_data 
            });
        }
    }

    fetchPMViewData = async () => {
        try {
            const authHeaders = await this.getAuthHeaders();
            const response = await Axios.get(`${API_URL}pm_view/`, authHeaders);
            
            // Filter out dead/completed projects
            const excludedStages = ['Dead', 'Prospecting Dead', 'Completed Jobs', 'Closed Lost','Completed'];
            response.data.forEach(e => {
                e.requirements_assigned = e.requirements_assigned.filter(el => 
                    !excludedStages.includes(el['stage'])
                );
            });

            // Sort by number of assignments
            response.data.sort((a, b) => b.requirements_assigned.length - a.requirements_assigned.length);
            
            // Extract unique values for filter lists
            const extractUniqueValues = (data, field) => 
                [...new Set(data.map(a => a[field]))]
                    .sort()
                    .filter(Boolean)
                    .map(x => ({ title: x }));

            const extractJobValues = (data, field) =>
                [...new Set(data.flatMap(a => a.requirements_assigned.map(j => j[field])))]
                    .sort()
                    .filter(Boolean)
                    .map(x => ({ title: x }));

            this.setState({
                loading: false,
                pm_view_data: response.data,
                filtered_pm_view_data: response.data,
                supervisors_pm_list: extractUniqueValues(response.data, 'supervisor'),
                locations_pm_list: extractUniqueValues(response.data, 'location_short'),
                divisions_pm_list: extractUniqueValues(response.data, 'division'),
                divisions_job_list: SFDC_DIVISIONS,
                accounts_job_list: extractJobValues(response.data, 'sfdc_account_name'),
                stages_job_list: SFDC_STAGE_OPTIONS,
                companies_job_list: SFDC_COMPANIES,
            }, () => {
                // Apply filters after state has been updated
                this.applyFilters(this.state.filters);
            });
        } catch (error) {
            console.error('Error fetching PM view data:', error);
            this.setState({ error: error, loading: false });
        }
    }

    fetchPMResourceNames = async () => {
        try {
            const authHeaders = await this.getAuthHeaders();
            const response = await Axios.get(`${API_URL}pm_resources_names/`, authHeaders);
            
            response.data.sort((a, b) => a.full_name.localeCompare(b.full_name));
            
            this.setState({ pmResourceNames: response.data });
        } catch (error) {
            console.error('Error fetching PM resource names:', error);
        }
    }

    fetchFilterData = async () => {
        const userId = localStorage.getItem('id');
        if (!userId) return;

        try {
            const authHeaders = await this.getAuthHeaders();
            
            // Fetch filter data
            const filterResponse = await Axios.get(`${API_URL}pm_view_filters/${userId}/`, authHeaders);
            if (filterResponse.status === 200) {
                this.setState({ filters: filterResponse.data.filters });
                if (!this.state.loading) {
                    this.applyFilters(filterResponse.data.filters);
                }
            }

            // Fetch profile data
            const profileResponse = await Axios.get(`${API_URL}profile/`, authHeaders);
            if (profileResponse.status === 200) {
                this.setState({ profile_data: profileResponse.data });
            }
        } catch (error) {
            console.error('Error fetching filter/profile data:', error);
            this.setState({ error: error });
        }
    }
    
    componentDidMount() {
        document.title = "PM View";
        this.fetchPMViewData();
        this.fetchPMResourceNames();
        this.fetchFilterData();
    }

    render() {
        return (
            <Root>
                <div className={classes.root_grid}>
                    <div className={classes.header}>
                        <Header />
                    </div>
                    <div>
                        {this.state.offline ? (
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                You are offline!
                            </div>
                        ) : this.state.error ? (
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                <Typography variant="h5" style={{ fontFamily: 'inherit' }} gutterBottom>
                                    <strong>An error occured!</strong>
                                </Typography>
                                <center>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={() => window.location.reload()}
                                    >
                                        <Replay /> Try again
                                    </Button>
                                </center>
                            </div>
                        ) : (      
                            <>
                                <Sidebar />  
                                <div className={classes.main}>
                                    <Paper className={classes.pm_view}>
                                    <PMs 
                                        pm_view_data={this.state.filtered_pm_view_data}
                                        loading={this.state.loading}
                                        sort_by={this.state.sort_by}
                                        handleSortByChange={this.handleSortByChange}     
                                        showDetailedView = {this.state.showDetailedView}
                                        toggleDetailView = {this.toggleDetailView}   
                                        updateDetailView = {this.updateDetailView}
                                        filters={this.state.filters}   
                                    />
                                </Paper>
                                {this.state.showDetailedView ?  
                                <>
                                    <Dialog
                                        open={this.state.showDetailedView}
                                        fullWidth={true}
                                        maxWidth={'lg'}
                                    >
                                        <ProjectDetailedViewContainer
                                            loading={this.state.loading}
                                            detail_view_project_info={this.state.detail_view_project_info}
                                            toggleDetailView={this.toggleDetailView}   
                                            pmResourceNames={this.state.pmResourceNames}  
                                            updateDetailView = {this.updateDetailView}  
                                            fetchPMViewData = {this.fetchPMViewData}
                                            multiply_by_sfdc_probability = {this.state.filters.multiply_by_sfdc_probability}
                                            profile_data={this.state.profile_data.find(e => e.id === this.state.detail_view_project_info.profile_type)}
                                            auth = {this.props.auth}
                                        />
                                    </Dialog>
                                </>
                                : (<></>)}
                            </div>
                                <PMSidebar
                                    loading={this.state.loading}           
                                    filters={this.state.filters} 
                                    divisions_pm_list={this.state.divisions_pm_list} 
                                    supervisors_pm_list={this.state.supervisors_pm_list} 
                                    locations_pm_list={this.state.locations_pm_list} 
                                    companies_job_list={this.state.companies_job_list}
                                    divisions_job_list={this.state.divisions_job_list}
                                    accounts_job_list={this.state.accounts_job_list}
                                    stages_job_list={this.state.stages_job_list}
                                    updateFilters={this.updateFilters} 
                                />
                            </>
                            )}
                        </div>
                    <div>
                        <Footer />
                    </div>                
                </div>
            </Root>
        );
    }
}

export default (PMsContainer)
